/**
 * The administration shell: what the rail shows, and what each door says
 * (rail of issue #24; destinations of refonte modulaire spec §15).
 *
 * ## Two lists, and why they are still two
 *
 * `ADMIN_RAIL` is what the rail shows, `ADMIN_DOORS` is what a path is called
 * at the door, and `admin-nav.test.ts` asserts that every rail target resolves
 * to a door of its own rather than to the generic root.
 *
 * They stayed split through the coupe (issue #32) for the reason that outlived
 * the experience editor: Réglages REGROUPS its screens, so the door names a
 * sub-address the rail has no entry for. The editor - a door without a rail
 * entry - is gone with the wizard.
 *
 * Same régime as `MODEL_SETTINGS`: the French sits in the catalogue because a
 * second spelling would be a second meaning.
 *
 * ## What the coupe changed here
 *
 * §15 sets the eight destinations of TODAY. The coupe itself only stood up
 * seven: six were already standing, and the seventh, **Personas**, is added
 * by the coupe rather than by the lot that builds its screen - the order of
 * the rail is what an operator learns once, and a rail that gains an icon
 * three lots later is an order everyone has to relearn. The same argument
 * posed Critères in #24. Médiathèque is the eighth, landing later still - see
 * below.
 *
 * What LEFT are the doors of screens that no longer exist: the overview, the
 * results, the composer, the ambiance and scripts tabs of Réglages, the cycle
 * thresholds, and the four pre-BUILD-851 redirections. A door that named a
 * deleted screen would be an address an operator could still be sent to.
 *
 * ## Médiathèque (issue #121)
 *
 * The eighth destination, and the first to land UNDER the spacer beside
 * Réglages rather than in the top group: a file is not one of the six
 * versioned objects (§16, H7 - the library keeps its code through the coupe
 * but restarts empty), so it has no filtered grid and no version history, and
 * §15's argument that put Personas in the top group does not carry it there.
 */

/**
 * A door: what the login card says while it asks who is connected.
 */
export interface AdminDoor {
  /** The heading of the login card. */
  readonly gateTitle: string;
  /** The verb on its button. */
  readonly gateAction: string;
}

/** A door, plus the path prefix it answers for. */
export interface AdminDoorEntry extends AdminDoor {
  readonly path: string;
}

/**
 * The eight destinations of the rail (spec §15, §16 - Médiathèque added by
 * issue #121).
 *
 * The identifier is what the component branches on rather than the path,
 * because `activeRailId` answers with an id.
 */
export type AdminRailId =
  | 'sessions'
  | 'interviews'
  | 'experiences'
  | 'protocol'
  | 'criteria'
  | 'personas'
  | 'mediatheque'
  | 'settings';

/**
 * The targets a rail entry may carry. A union so a typo cannot reach the
 * router.
 */
export type AdminRailPath =
  | '/admin/sessions'
  | '/admin/entretiens'
  | '/admin/experiences'
  | '/admin/protocoles'
  | '/admin/criteres'
  | '/admin/personas'
  | '/admin/mediatheque'
  | '/admin/reglages';

/**
 * Which drawing an entry wears.
 *
 * A NAME and not a component, so this module stays pure and testable.
 * `admin-rail.tsx` maps the name to its `lucide-react` component, and it is the
 * only file in the shell that imports an icon.
 */
export type AdminRailIcon = AdminRailId;

export interface AdminRailEntry {
  readonly id: AdminRailId;
  /**
   * What the entry is CALLED. It is never painted - the rail draws an icon -
   * so this string reaches a human only through `aria-label` and `title`.
   *
   * Which makes it a contract with the e2e suite rather than decoration:
   * `getByRole('link', { name })` reads the accessible name. Changing one of
   * these strings is changing a selector.
   */
  readonly label: string;
  readonly to: AdminRailPath;
  readonly icon: AdminRailIcon;
}

/**
 * The six destinations of the top group, in order.
 *
 * Réglages is NOT here: it sits under the spacer, in `ADMIN_RAIL_SETTINGS`, so
 * the rail can push it and the sign-out to the bottom edge. Two lists rather
 * than a `position: 'bottom'` flag, because the bottom group is not a variant
 * of the top one - it holds a destination AND an action, and the action is not
 * a link at all.
 */
export const ADMIN_RAIL: readonly AdminRailEntry[] = [
  /*
   * The sessions (§12, §15). « Le renommage emporte l'adresse » (grill du
   * 2026-09-14): the entry is called Sessions AND it leads to
   * `/admin/sessions`, both moved by the lot that rewrites the screen - a rail
   * naming « Sessions » an address still spelled `journal` would be half a
   * gesture, and the half nobody would finish.
   *
   * `/admin/journal` does not disappear: it REDIRECTS, keeping its
   * `?session=`. That address circulated in team threads, and a pasted link
   * has to keep landing somewhere.
   */
  { id: 'sessions', label: 'Sessions', to: '/admin/sessions', icon: 'sessions' },
  /*
   * The blind interviews (chat spec §12, issue #11), and the one entry of the
   * seven whose screens are REAL after the coupe.
   */
  { id: 'interviews', label: 'Entretiens', to: '/admin/entretiens', icon: 'interviews' },
  { id: 'experiences', label: 'Expériences', to: '/admin/experiences', icon: 'experiences' },
  { id: 'protocol', label: 'Protocoles', to: '/admin/protocoles', icon: 'protocol' },
  { id: 'criteria', label: 'Critères', to: '/admin/criteres', icon: 'criteria' },
  /*
   * Personas (spec §10), added by the coupe. Fifth of the four versioned
   * objects to get its place, and last of the top group, so an entry added
   * later does not renumber what an operator already knows.
   */
  { id: 'personas', label: 'Personas', to: '/admin/personas', icon: 'personas' },
];

/**
 * The bottom of the rail: what holds for the whole dispositif.
 *
 * Its own constant rather than the tail of `ADMIN_RAIL`, because the rail puts
 * a growing spacer between the two groups and a single list would have to
 * carry an index nobody could read.
 *
 * An ORDERED LIST of two since issue #121 (grill du 2026-09-22), not a single
 * entry: Médiathèque joins Réglages under the spacer, ahead of it, because
 * neither one is a destination of the six versioned libraries above - a file
 * is not versioned (§16, H7), and Réglages already groups what applies to the
 * whole dispositif. Both derive `ADMIN_RAIL_ALL`, `activeRailId` and
 * `doorForPath` the same way the top six always have - no parallel branch for
 * the bottom group.
 */
export const ADMIN_RAIL_SETTINGS: readonly AdminRailEntry[] = [
  {
    id: 'mediatheque',
    label: 'Médiathèque',
    to: '/admin/mediatheque',
    icon: 'mediatheque',
  },
  {
    id: 'settings',
    label: 'Réglages',
    to: '/admin/reglages',
    icon: 'settings',
  },
];

/**
 * The eight destinations in reading order, top group then bottom.
 *
 * Sign-out is NOT here and never will be: it is an action, it has no path, and
 * on a phone it is not in the rail at all - it surfaces inside Réglages
 * instead.
 */
export const ADMIN_RAIL_ALL: readonly AdminRailEntry[] = [...ADMIN_RAIL, ...ADMIN_RAIL_SETTINGS];

/**
 * The root door: `doorForPath` falls back to it, and a fallback that can be
 * `undefined` would push the check onto every caller.
 *
 * It names the SESSIONS, because `/admin` redirects there.
 */
const ROOT_DOOR: AdminDoor = {
  gateTitle: 'Sessions',
  gateAction: 'Ouvrir les sessions',
};

/**
 * What each path is called at the door.
 *
 * Order does not matter: `doorForPath` scans longest path first, so
 * `/admin/reglages/modeles` can never be answered for by `/admin/reglages`.
 */
export const ADMIN_DOORS: readonly AdminDoorEntry[] = [
  /*
   * Les sessions (§12.1). Une seule porte pour les trois écrans du lot - la
   * liste, le parcours de `/admin/sessions/<id>` et sa conversation - parce
   * que le balayage ci-dessous est un balayage de PRÉFIXE et que les trois sont
   * la même lecture. Une porte qui nommerait une session par son identifiant
   * n'apprendrait rien à qui ne s'est pas encore connecté.
   *
   * `/admin/journal` n'en a plus : sa redirection se résout AVANT que la porte
   * ne soit peinte, donc c'est la destination qui se nomme, le régime que
   * `reglages.index.tsx` a déjà posé.
   */
  { path: '/admin/sessions', gateTitle: 'Sessions', gateAction: 'Ouvrir les sessions' },
  /*
   * ONE door for the listing and for a transcript (issue #11): the scan below is
   * a prefix scan, so `/admin/entretiens/<id>` is answered for by this entry.
   * That is the right answer rather than a shortcut - both screens are the same
   * reading, and a door that named a room by its identifier would be showing an
   * identifier §5 of the chat spec keeps off every participant projection to
   * somebody who has not signed in yet.
   */
  {
    path: '/admin/entretiens',
    gateTitle: 'Entretiens à l’aveugle',
    gateAction: 'Ouvrir les entretiens',
  },
  {
    path: '/admin/experiences',
    gateTitle: 'Catalogue des expériences',
    gateAction: 'Ouvrir le catalogue',
  },
  {
    path: '/admin/protocoles',
    gateTitle: 'Éditeur de protocole',
    gateAction: 'Ouvrir les protocoles',
  },
  /*
   * The five coutures have doors like any other screen, because a door that
   * fell back to the root here would tell an operator following a link that
   * they were opening the journal.
   */
  {
    path: '/admin/criteres',
    gateTitle: 'Bibliothèque de critères',
    gateAction: 'Ouvrir les critères',
  },
  {
    path: '/admin/personas',
    gateTitle: 'Bibliothèque de personas',
    gateAction: 'Ouvrir les personas',
  },
  /*
   * Médiathèque (§16, H7, issue #121) : le fichier n'est pas versionné, donc
   * cette porte n'a pas de sixième voisine parmi les bibliothèques ci-dessus -
   * elle est nommée ici comme n'importe quel autre écran plutôt que de
   * retomber sur la racine.
   */
  {
    path: '/admin/mediatheque',
    gateTitle: 'Médiathèque',
    gateAction: 'Ouvrir la médiathèque',
  },
  {
    path: '/admin/reglages/modeles',
    gateTitle: 'Modèles du dispositif',
    gateAction: 'Ouvrir les modèles',
  },
  /*
   * The second tab of Réglages (§11.5, issue #41). Its own door rather than a
   * fallback on `/admin/reglages`, for the reason the coutures have one: a door
   * that named « les réglages » on a link somebody was sent to would not say
   * which of the two tabs is opening.
   *
   * Its wording is deliberately NOT « Ouvrir les personas » - that belongs to
   * `/admin/personas`, the library, and two doors answering to one name would
   * make `getByRole('button', { name })` ambiguous the day a spec visits both.
   */
  {
    path: '/admin/reglages/personas',
    gateTitle: 'Réglages des personas',
    gateAction: 'Ouvrir les réglages des personas',
  },
  {
    path: '/admin/reglages',
    gateTitle: 'Réglages du dispositif',
    gateAction: 'Ouvrir les réglages',
  },
];

/** Longest first, so a sub-path is never answered for by its parent. */
const DOORS_BY_DEPTH: readonly AdminDoorEntry[] = [...ADMIN_DOORS].sort(
  (a, b) => b.path.length - a.path.length,
);

/**
 * Which screen a path belongs to, for the door to name it.
 *
 * The root is matched by EXCLUSION rather than by prefix, and that asymmetry is
 * the point: `'/admin/sessions'.startsWith('/admin')` is true, so a plain prefix
 * scan would hand every screen the root's wording - the exact flattening this
 * catalogue exists to avoid. An unknown path under `/admin` falls back to the
 * root, which is the honest answer: we do not know where it is going.
 */
export function doorForPath(pathname: string): AdminDoor {
  const match = DOORS_BY_DEPTH.find((door) => pathname.startsWith(door.path));
  return match ?? ROOT_DOOR;
}

/**
 * Which entry the rail lights up, decided HERE rather than by the router.
 *
 * The router cannot decide it: its notion of active is « the path is a prefix
 * of the location », and this rail has entries that notion gets wrong.
 * « Réglages » is a prefix of its sub-screens, and `/admin` is a prefix of
 * every screen there is.
 *
 * `null` is a legitimate answer - a screen with no rail entry lights none, and
 * lighting one at random would be worse. The defect this design replaces was
 * real: two `aria-current="page"` on one page, shipped green through both the
 * unit tests and the e2e assertions, caught on the MR screenshots.
 */
export function activeRailId(pathname: string): AdminRailId | null {
  /*
   * `/admin` redirects into the sessions, and a redirection resolves before the
   * screen paints. It is answered for anyway: the shell renders its rail around
   * the DOOR as well as around a screen, so an operator who follows that
   * address while signed out would otherwise meet a rail with nothing lit.
   *
   * `/admin/journal` is answered for here too, and for the same reason: it
   * redirects into `/admin/sessions` (§15), and the rail must not go dark on
   * the frame a pasted team link lands on.
   *
   * Exact for the root, and tested FIRST: every admin path starts with `/admin`,
   * so the prefix scan below would never see it, and a prefix test on it would
   * light Sessions on all six others.
   */
  if (pathname === '/admin' || pathname === '/admin/') return 'sessions';
  if (pathname.startsWith('/admin/journal')) return 'sessions';
  /*
   * DERIVED from the catalogue rather than restated entry by entry, so that a
   * path renamed or added in `ADMIN_RAIL` lights its entry without a second
   * list to keep in step - the drift that would leave a rail dark on a screen
   * and pass every test that only asserts « at most one entry lit ». A prefix
   * scan is enough because no entry's `to` is a prefix of another's; the
   * listing and a transcript of `/admin/entretiens/<id>` light the same entry
   * by that same rule.
   */
  return ADMIN_RAIL_ALL.find((entry) => pathname.startsWith(entry.to))?.id ?? null;
}
